import React, { useMemo } from 'react'
import moment from 'moment'

import { EmptyObject } from 'lib/object/objectUtils'
import { sumUpOccupancies } from 'lib/offer/occupancyUtils'
import { useDirectSearchPrices } from 'hooks/Search/useSearchPrices'
import Group from 'components/utils/Group'
import LoadingIndicator from 'components/Common/Loading/LoadingIndicator'
import PriceRowPriceCaptionLoadingSkeleton from 'LuxKit/PricePoints/PriceRowPriceCaptionLoadingSkeleton'
import PriceRowPriceLoadingSkeleton from 'LuxKit/PricePoints/PriceRowPriceLoadingSkeleton'
import PriceRowValueDiscountWithCaptionLoadingSkeleton from 'LuxKit/PricePoints/Value/PriceRowValueDiscountWithCaptionLoadingSkeleton'
import OfferTilePricing from 'components/OfferList/OfferListTiles/Support/OfferTilePricing'
import SearchTileFlightOnlyPricing from 'components/OfferList/OfferListTiles/Support/SearchTileFlightOnlyPricing'
import SearchTileWalledMessage from '../shared/SearchTileWalledMessage'
import WalledContent from 'components/Common/WalledContent'
import { calculateOfferTileDiscountPercentage } from 'lib/search/searchUtils'
import useOfferMetaData from 'hooks/Offers/useOfferMetaData'
import { useAppSelector } from 'hooks/reduxHooks'
import { getCharterBundleRoute } from 'selectors/offerPage/initialOfferPageStateSelectors'
import { useMaldivesBundleContext } from 'components/Pages/HotelSearchPage/MaldivesBundleContext'
import { useMaldivesDirectBundleContext } from 'components/MaldivesDirect/MaldivesDirectSearchPage/MaldivesDirectBundleContext'
import { useMaldivesOffersPricingContext } from 'components/MaldivesDirect/MaldivesDirectSearchPage/hooks/useMaldivesOffersPricing'
import CharterBundlePricing from './CharterBundlePricing'

interface Props {
  bestPackage?: App.HotelPackage
  filters?: App.OfferListFilters
  offer: App.HotelOffer | App.HotelOfferSummary | App.VillaOffer | App.VillaOfferSummary
  bestRate?: App.OfferAvailableRate
  duration?: number
  hidePrice?: boolean
  showPerNightPricing?: boolean
  hideIncludesTaxesAndFees?: boolean
}

function HotelSearchTilePriceStack({
  bestPackage,
  filters,
  offer,
  bestRate,
  duration,
  hidePrice = false,
  showPerNightPricing = false,
  hideIncludesTaxesAndFees = false,
}: Props) {
  const hasDates = !!(filters?.checkIn && filters?.checkOut && filters?.rooms?.length)
  const checkInDate = useMemo(() => {
    return filters?.checkIn ? moment(filters.checkIn) : undefined
  }, [filters?.checkIn])

  const directSearchPrices = useDirectSearchPrices({ filters: filters ?? EmptyObject, offerId: offer.id })
  const discountPercentage = useMemo(() => {
    if (offer.isDiscountPillHidden) {
      return undefined
    }

    if (directSearchPrices?.lowestPrice && directSearchPrices?.lowestPriceValue) {
      return calculateOfferTileDiscountPercentage({
        price: directSearchPrices.lowestPrice,
        value: directSearchPrices.lowestPriceValue,
      })
    }

    if (bestPackage) {
      return calculateOfferTileDiscountPercentage({
        price: bestPackage.price,
        value: bestPackage.value,
      })
    }

    return undefined
  }, [bestPackage, directSearchPrices, offer])

  const offerMetaData = useOfferMetaData(offer.id, filters)

  const charterBundleRoute = useAppSelector(state => getCharterBundleRoute(state, offer))
  const { isBundleMode } = useMaldivesBundleContext()
  const { charterBundle, isPricePerPerson } = useMaldivesDirectBundleContext()
  // Show SearchTileFlightOnlyPricing only when it's Maldives bundle mode OR bundled with flights only
  // If charterBundle is true, always use OfferTilePricing instead of FlightOnlyPricing
  const shouldShowFlightOnlyPricing = !charterBundle && ((charterBundleRoute && isBundleMode) || offer.bundledWithFlightsOnly)

  // Get pricing from MaldivesOffersPricingContext for charter bundle offers
  const { pricingByOfferId, isLoading: isContextLoading } = useMaldivesOffersPricingContext()
  const contextPricing = pricingByOfferId[offer.id]

  // Calculate total guests (adults + children + infants) for Maldives Charter bundle
  const totalGuests = useMemo(() => {
    if (!filters?.rooms) return undefined
    return sumUpOccupancies(filters.rooms)
  }, [filters?.rooms])

  // For charter bundle offers, use context pricing if available
  if (charterBundle && contextPricing) {
    const isLoading = isContextLoading || contextPricing.isLoading
    if (isLoading) {
      return (
        <Group direction="vertical" gap={4}>
          <PriceRowPriceCaptionLoadingSkeleton />
          <PriceRowPriceLoadingSkeleton size="L" />
          <PriceRowValueDiscountWithCaptionLoadingSkeleton />
        </Group>
      )
    }
    if (contextPricing.hasAvailability && contextPricing.bundleTotalPrice) {
      const nightsFromPricing = contextPricing.allocatedPackages[0]?.package?.duration
      return <CharterBundlePricing
        offer={offer}
        pricing={contextPricing}
        isPricePerPerson={isPricePerPerson}
        discountPercentage={contextPricing.discountPercent}
        nights={nightsFromPricing ?? duration}
      />
    }
  }

  return <div>
    {!bestPackage && <LoadingIndicator inline ballSize={12} />}
    {bestPackage && <WalledContent
      enforced={offer.walledGarden}
      wall={<SearchTileWalledMessage discountPercentage={discountPercentage} />}
    >
      <Group direction="vertical" gap={8} fullWidth>
        {!shouldShowFlightOnlyPricing && <OfferTilePricing
          offer={offer}
          hasDates={hasDates}
          checkInDate={checkInDate}
          pkg={bestPackage}
          availableRate={bestRate}
          align="start"
          filters={filters}
          duration={duration}
          hidePrice={hidePrice}
          overrideRoomsNumber={offerMetaData?.rooms.length}
          isPricePerNight={showPerNightPricing}
          hideIncludesTaxesAndFees={hideIncludesTaxesAndFees}
          totalGuests={totalGuests}
        />}
        {(shouldShowFlightOnlyPricing && !hidePrice) && (
          <SearchTileFlightOnlyPricing
            offer={offer}
            rooms={filters?.rooms}
            hasDates={hasDates}
            pkg={bestPackage}
            pricing={bestRate}
            filters={filters}
          />
        )}
      </Group>
    </WalledContent>}
  </div>
}

export default HotelSearchTilePriceStack
